import AddIcon from '@mui/icons-material/AddCircle';
import ChatIcon from '@mui/icons-material/Chat';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Checkbox, Divider, FormControl, FormControlLabel, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import Grid from "@mui/material/Grid/Grid";
import TextField from "@mui/material/TextField/TextField";
import { makeStyles, useTheme } from "@mui/styles";
import moment from "moment";
import { useSnackbar } from "notistack";
import React, { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import * as Yup from 'yup';
import AttachmentPreviewBox from "../../../components/AttachmentPreviewBox";
import CommentFormDialog from "../../../components/CommentFormDialog";
import DatePicker from "../../../components/DatePicker";
import DefaultButton from "../../../components/FormControl/DefaultButton";
import ErrorButton from "../../../components/FormControl/ErrorButton";
import Form, { FormDirty, FormErrors, FormFooterAllButtons, FormValidationSchema } from "../../../components/FormControl/Form/Form";
import FormDialog from '../../../components/FormControl/FormDialog';
import { InputComponentPrice } from "../../../components/InputComponentPrice";
import ListTextField from "../../../components/ListTextField";
import MultiSelect, { MultiSelectOption } from '../../../components/MultiSelect';
import Panel from "../../../components/Panel/Panel";
import PanelBodyFormContent from "../../../components/Panel/PanelBodyFormContent";
import { PopupWrapper } from "../../../components/PopupWrapper";
import SelectSearch from "../../../components/SelectSearch";
import SearchEntity from "../../../components/SelectSearch/SearchEntity";
import TableListPsim from "../../../components/TableListPsim/TableListPsim";
import TicketHistory, { TicketHistoryEventEmitterType } from "../../../components/TicketHistory";
import TicketStatusIcon from "../../../components/TicketStatusIcon";
import SoftwareVersionType from "../../../enum/SoftwareVersionType";
import UserType from "../../../enum/UserType";
import AttachmentTechnicalAssistance from "../../../model/AttachmentTechnicalAssistance";
import Charge from "../../../model/Charge";
import CommentTechnicalAssistance from "../../../model/CommentTechnicalAssistance";
import Component from "../../../model/Component";
import DamagedCircuitBlock from "../../../model/DamagedCircuitBlock";
import FinalCustomer from "../../../model/FinalCustomer";
import Integrator from "../../../model/Integrator";
import Product from "../../../model/Product";
import Production from "../../../model/Production";
import RelatedProblem from "../../../model/RelatedProblem";
import Software from "../../../model/Software";
import TechnicalAssistance from "../../../model/TechnicalAssistance";
import TicketOrigin from "../../../model/TicketOrigin";
import TicketStatus from "../../../model/TicketStatus";
import User from "../../../model/User";
import { useAuthentication } from "../../../reducer/authentication";
import { WhereCondition, downloadBlob } from "../../../service/ServiceApi";
import { ServiceApi } from "../../../service/ServiceApiWithLoading";
import { convertFileByteToSizeString, formatCurrency, objectClone, sortObjectArrayByProp } from "../../../utils";
import EventEmitter from "../../../utils/EventEmitter";
import ComponentTechnicalAssistance from '../../../model/ComponentTechnicalAssistance';
import TechAssisComponentFormDialog, { TechAssisComponentFormDialogData } from './TechAssisComponentFormDialog';
import confirmation from '../../../components/ConfirmationDialog';

const apiComponent = new ServiceApi<Component>('component');
const apiDamagedCircuitBlock = new ServiceApi<DamagedCircuitBlock>('damagedCircuitBlock');
const apiRelatedProblem = new ServiceApi<RelatedProblem>('relatedProblem');
const apiCharge = new ServiceApi<Charge>('charge');
const apiTicketStatus = new ServiceApi<TicketStatus>('ticketStatus');
const api = new ServiceApi<TechnicalAssistance>('technicalAssistance');
const apiProduct = new ServiceApi<Product>('product');
const apiFinalCustomer = new ServiceApi<FinalCustomer>('finalCustomer');
const apiIntegrator = new ServiceApi<Integrator>('integrator');
const apiProduction = new ServiceApi<Production>('production');

interface TechnicalAssistanceRow {
    id: number;
    name: React.ReactNode;
    status: React.ReactNode;
    created_date: React.ReactNode;
    product_name: string;
    cost_amount: string;
    last_update_status: string;
    identified_problem: string;
    necessary_procedure: string;
    circuits: string;
}

export interface AttachmentTechnicalAssistanceRow extends AttachmentTechnicalAssistance {
    edited?: boolean;
}

export type TechnicalAssistanceFormData = {
    name: string;
    ticket_status_id: number | null;
    product_id: number | null;
    equipment_return: boolean;
    is_recurrent_assistance: boolean | null;
    technical_assistance_id: number | null;
    without_invoice: boolean;
    invoice_number: string;
    observation_invoice: string;
    without_seal: boolean;
    seal: string;
    seal_old: string;
    user_id: number | null;
    charge_id: number | null;
    final_customer_id: number | null;
    ticket_type_id: number | null;
    integrator_id: number | null;
    ticket_cause_id: number | null;
    ticket_origin_id: number | null;
    software_version_id: number | null;
    identified_problem: string;
    necessary_procedure: string;
    firmware_updated: number;
    factory_reset: number;
    ip: string;
    tracking_post_office: string;
    post_office_date: Date | null;
    components: ComponentTechnicalAssistance[];
    damaged_circuit_blocks: number[];
    related_problems: number[];
    attachments: AttachmentTechnicalAssistanceRow[];
    comments: CommentTechnicalAssistance[];
    additional_item_received: string;
    cost_materials: number | null;
    cost_materials_old: number | null;
    cost_freight: number | null;
    cost_freight_old: number | null;
    cost_amount: number | null;
    created_at: string;
    description_customer_problem: string;
    resolution_observation: string;
    seals: string[];
    local_pickup: boolean;
    declaration_content: boolean;
    service_invoice: string | null;
    invoice_number_out: string | null;
    available_for_pickup: boolean;
    not_charged: boolean;
    customer_contacts: number[];
}

export interface TechnicalAssistanceFormProps {
    title: React.ReactNode;
    item?: TechnicalAssistance;
    onSubmit: (data: TechnicalAssistanceFormData, attachmentsRemoved: AttachmentTechnicalAssistanceRow[], isDirty?: boolean, isStatusChanged?: boolean, submitType?: string | null) => Promise<any>;
    onCancel: () => any;
    onChange?: (changed: boolean) => any;
    onUndo?: () => any;
    defaultTicketStatusId?: number | null;
}

interface AttachmentDescriptionFormDialogData {
    description: string;
}

export interface AttachmentDescriptionFormDialogProps {
    attachmentDescriptionDialogOpen: boolean;
    onSubmit: (description: string) => any;
    onCancel: () => any;
    attachments: AttachmentTechnicalAssistanceRow[];
    attachmentsEditingIdx: number | null;
}

function AttachmentDescriptionFormDialog(props: AttachmentDescriptionFormDialogProps) {

    const { attachmentDescriptionDialogOpen, onCancel, attachments, attachmentsEditingIdx, onSubmit } = props;

    function onSubmitHandler(data: AttachmentDescriptionFormDialogData) {
        onSubmit(data.description);
    }

    function getDescription(): string {
        if (attachmentsEditingIdx === null) return '';

        return attachments[attachmentsEditingIdx].description;
    }

    return (
        <FormDialog<AttachmentDescriptionFormDialogData>
            show={attachmentDescriptionDialogOpen}
            title={'Descrição do anexo'}
            dismiss={() => null}
            onSubmit={(values, errors) => {
                const { description } = values;
                onSubmitHandler({ description });
            }}
            initialValues={{ description: getDescription() }}
            enableReinitialize={true}
            maxWidth='sm'
            fullWidth
        >
            {({ values, errors, handleChange, setFieldValue, isSubmitting, dirty }) => (
                <>
                    <FormControl fullWidth variant="outlined" margin='dense'>
                        <TextField
                            name="description"
                            label={"Descrição"}
                            margin="dense"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                            value={values.description}
                            onChange={handleChange}
                            error={!!errors.description && !!dirty.description}
                            helperText={!!errors.description && !!dirty.description && errors.description}
                        />
                    </FormControl>
                    <FormControl fullWidth variant="outlined" margin="dense" style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                        <DefaultButton
                            size='small'
                            type='submit'
                            variant="contained"
                            disabled={isSubmitting}>
                            OK
                        </DefaultButton>
                        <ErrorButton
                            size='small'
                            variant="contained"
                            disabled={isSubmitting}
                            onClick={() => onCancel()}
                            style={{ marginLeft: 10 }}>
                            Cancelar
                        </ErrorButton>
                    </FormControl>
                </>
            )}
        </FormDialog>
    );
}

interface AttachmentListProps {
    attachments: AttachmentTechnicalAssistanceRow[];
    downloadAttachment: (name: string) => void;
    setFieldValue: any;
    openAttachmentDescriptionDialog: (idx: number) => void;
    removeAttachment: (idx: number, attachments: AttachmentTechnicalAssistanceRow[], setFieldValue: any) => void;
    addAttachment: (attachments: AttachmentTechnicalAssistanceRow[], setFieldValue: any, name: string, file: File) => void;
    title: string;
    removeAddButton: boolean;
}

function AttachmentList(props: AttachmentListProps) {
    const { attachments, downloadAttachment, setFieldValue, openAttachmentDescriptionDialog, removeAttachment, addAttachment, title, removeAddButton } = props;
    let contadorAttachments = 0;

    var refInputFile = React.createRef<HTMLInputElement>();

    function inputChangeHandler(e: ChangeEvent<HTMLInputElement>) {
        const input = e.target;
        if (input?.files && input.files.length > 0) {
            Array.from(input.files).forEach(file => {
                addAttachment(attachments, setFieldValue, file.name, file);
            });
        }
    }

    function buttonClickHandler(e: MouseEvent<HTMLButtonElement>) {
        const input = refInputFile.current;
        input?.click();
    }

    return (<>
        <Box marginTop={3} marginBottom={0} display="flex" >
            <Typography style={{ flex: 1 }} variant='subtitle2' className="subtitleGradientBackgorund">{title}</Typography>
            {removeAddButton === false ? <Tooltip placement="top" title={'Adicionar anexo'}>
                <IconButton
                    size='small'
                    color="inherit"
                    aria-label={'Adicionar anexo'}
                    onClick={buttonClickHandler}
                >
                    <input
                        ref={refInputFile}
                        type='file'
                        style={{ display: 'none' }}
                        onChange={inputChangeHandler}
                        multiple={true}
                        accept={"*"}
                    />
                    <AddIcon />
                </IconButton>
            </Tooltip> : null}
        </Box>
        <Divider style={{ marginBottom: 4 }} />
        {attachments.map((at, idx) => {
            return <Box key={idx + '-attachment'} marginBottom={3} display="flex">
                <div style={{ fontWeight: 'bold', display: 'flex', width: 35, alignItems: 'center' }} >#{`${++contadorAttachments}`}</div>
                <Box marginBottom={3} display="flex" flexDirection="column" flex={1} margin={0} overflow="hidden">
                    <div style={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                        <AttachmentPreviewBox id={at.technical_assistance_id || null} model="TechnicalAssistance" name={at.name} />
                        <Typography style={{ flex: 1, alignItems: 'center', display: 'flex', overflow: 'hidden', paddingLeft: 5 }} title={at.name} variant='body2'>{at.name}</Typography>
                        {at.file_size ? <Typography style={{ alignItems: 'center', display: 'flex', overflow: 'hidden', paddingRight: 5, paddingLeft: 5 }} variant='body2'>{convertFileByteToSizeString(at.file_size)}</Typography> : null}
                        {at.name && at.id > 0 ? <>
                            <Tooltip placement="top" title={'Download do anexo'}>
                                <IconButton
                                    size='small'
                                    color="inherit"
                                    aria-label={'Download do anexo'}
                                    onClick={() => downloadAttachment(at.name)}
                                >
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                        </> : null}
                        <Tooltip placement="top" title={'Adicionar informação'}>
                            <IconButton
                                size='small'
                                color="inherit"
                                aria-label={'Adicionar informação'}
                                onClick={() => openAttachmentDescriptionDialog(idx)}
                            >
                                <ChatIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip placement="top" title={'Remover anexo'}>
                            <IconButton
                                size='small'
                                color="inherit"
                                aria-label={'Remover anexo'}
                                onClick={() => removeAttachment(idx, attachments, setFieldValue)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                    {at.description ? <Typography style={{ flex: 1 }} variant='caption'>{at.description}</Typography> : null}
                </Box>
            </Box>;
        })}
    </>);
}

const eventEmmiter = new EventEmitter<TicketHistoryEventEmitterType>();
var timeoutBlurSeal = 0;

export async function loadLastTechAssisBySeal(sealValue: string, setFieldValue: any, setProduction: any, setDisableFieldIp: any, item?: TechnicalAssistance) {
    const where: WhereCondition<TechnicalAssistance> = [];
    where.push(['seal', '=', sealValue]);
    if (item?.id) {
        where.push(['id', '!=', item.id]);
    }
    var techAssisSeal: TechnicalAssistance | null = null;

    const techAssisSealList = await api.getList({ where, order: { created_at: 'DESC' }, limit: 1 });
    if (techAssisSealList.length > 0) {
        techAssisSeal = techAssisSealList[0];
    }
    const productionSeal = await getProductionBySerialNumber(sealValue, item);

    if (techAssisSeal || productionSeal) {
        await setFieldValue('equipment_return', true);
        await setFieldValue('integrator_id', techAssisSeal ? techAssisSeal.integrator_id : (productionSeal ? productionSeal.integrator_id : null));
        await setFieldValue('final_customer_id', techAssisSeal ? techAssisSeal.final_customer_id : null);
        let pId = techAssisSeal ? techAssisSeal.product_id : (productionSeal ? productionSeal.product_id : null);
        await setFieldValue('product_id', pId);

        let isHardwareIp = await getProductIsHardwareIp(pId);
        if (isHardwareIp === false) {
            setDisableFieldIp(true);
            await setFieldValue('ip', '');
        } else {
            setDisableFieldIp(false);
        }
        if (productionSeal) {
            let notCharged = false;
            // se rental_contrac, então não cobrar
            // se left_at (data de saída) for menor que 1 ano, então não cobrar
            if (productionSeal.rental_contract || (productionSeal.left_at && moment(productionSeal.left_at).isAfter(moment().subtract(1, 'year')))) {
                notCharged = true;
            }
            await setFieldValue('not_charged', notCharged);
        }
        setProduction(productionSeal);
    }
}

export async function getProductionBySerialNumber(serial_number?: number | string | null, item?: TechnicalAssistance): Promise<Production | null> {
    if (serial_number) {
        const whereProduction: WhereCondition<Production> = [];
        whereProduction.push(['serial_number', '=', serial_number]);
        if (item?.id) {
            whereProduction.push(['id', '!=', item.id]);
        }
        const productionSealList = await apiProduction.getList({ where: whereProduction, order: { created_at: 'DESC' }, limit: 1, relations: ['product_review_1', 'product_review_2', 'product_batch_1', 'product_batch_2', 'user', 'software_version', 'non_conformities'] });

        if (productionSealList.length > 0) {
            return productionSealList[0];
        }
    }
    return null;
}

export async function getProductIsHardwareIp(product_id?: number | null): Promise<boolean | null> {
    let isHardwareIp: boolean | null = null;
    if (product_id) {
        const product = await apiProduct.getById(product_id);
        isHardwareIp = product.is_hardware_ip;
    }
    return isHardwareIp;
}

const useStyles = makeStyles(theme => ({
    headerCell: {
        textAlign: 'center',
        color: theme.palette.background.default === '#202326' ? theme.palette.text.primary : '#031527',
        fontSize: '14px',
        fontWeight: 'bold',
        paddingRight: theme.spacing(3),
        height: theme.spacing(1),
        verticalAlign: 'middle'
    },
    bodyCell: {
        paddingRight: theme.spacing(3),
        height: theme.spacing(1),
        fontSize: theme.typography.fontSize,
        verticalAlign: 'middle'
    }
}));

export default function TechnicalAssistanceForm(props: TechnicalAssistanceFormProps) {

    const { title, item, onSubmit, onCancel, onChange, onUndo, defaultTicketStatusId } = props;

    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuthentication();
    const theme = useTheme();
    const classes = useStyles();

    const [damagedCircuitBlocks, setDamagedCircuitBlocks] = useState<MultiSelectOption<number>[]>([]);
    const [relatedProblems, setRelatedProblems] = useState<MultiSelectOption<number>[]>([]);
    const [attachmentsRemoved, setAttachmentsRemoved] = useState<AttachmentTechnicalAssistanceRow[]>([]);
    const [attachmentsEditingIdx, setAttachmentsEditingIdx] = useState<number | null>(null);
    const [attachmentDescriptionDialogOpen, setAttachmentDescriptionDialogOpen] = useState<boolean>(false);
    const [commentDialogOpen, setCommentDialogOpen] = useState<boolean>(false);
    const [isDirty, setDirty] = useState<boolean>(false);
    const [isFieldsMandatoryByTicketStatus, setIsFieldsMandatoryByTicketStatus] = useState<boolean>(false);
    const [disableFieldIp, setDisableFieldIp] = useState<boolean>(false);
    const [isMultiAdd, setIsMultiAdd] = useState<boolean>(false);
    const [production, setProduction] = useState<Production | null>(null);
    const [customerContacts, setCustomerContacts] = useState<MultiSelectOption<number>[]>([]);
    const [productKey, setProductKey] = useState<number>(0);

    const [finalCustomerTechnicalAssistanceObservation, setFinalCustomerTechnicalAssistanceObservation] = useState<string>('');
    const [finalCustomerTechnicalAssistanceObservationDialogOpen, setFinalCustomerTechnicalAssistanceObservationDialogOpen] = useState<boolean>(false);
    const [integratorTechnicalAssistanceObservation, setIntegratorTechnicalAssistanceObservation] = useState<string>('');
    const [integratorTechnicalAssistanceObservationDialogOpen, setIntegratorTechnicalAssistanceObservationDialogOpen] = useState<boolean>(false);

    const [technicalAssistanceSameSeal, setTechnicalAssistanceSameSeal] = useState<TechnicalAssistance[]>([]);
    const [hasRecentTechnicalAssistance, setHasRecentTechnicalAssistance] = useState(false);

    const [techAssisComponentDialogOpen, setTechAssisComponentDialogOpen] = useState<boolean>(false);

    useEffect(() => {
        loadCustomerContacts(item?.final_customer_id, item?.integrator_id, undefined, true);
        fetchDamagedCircuitBlocks();
        fetchRelatedProblems();
        setMandatoryFieldsByStatus(item?.ticket_status_id);
        getProductIsHardwareIp(item?.product_id).then((isHardwareIp) => {
            setDisableFieldIp(isHardwareIp === false);
        });
        fetchIntegratorTechAssisObs(item?.integrator_id);
        fetchFinalCustomerTechAssisObs(item?.final_customer_id);
        getTechnicalAssistanceSameSeal(item?.seal).then(setTechnicalAssistanceSameSeal);
        hasRecentTechnicalAssistanceBySeal(item?.seal).then(setHasRecentTechnicalAssistance);
        getProductionBySerialNumber(item?.seal, item).then(setProduction);
    }, []);

    async function loadCustomerContacts(final_customer_id?: number | null, integrator_id?: number | null, setFieldValue?: any, initialLoad?: boolean) {
        let users: User[] = [];
        if (integrator_id) {
            const integrator = await apiIntegrator.getById(integrator_id, { relations: ['users'] });
            users = [...(integrator.users || [])];
        }
        if (final_customer_id) {
            const finalCustomer = await apiFinalCustomer.getById(final_customer_id, { relations: ['users'] });
            users = [...users, ...(finalCustomer.users || [])];
        }
        let usersSorted = sortObjectArrayByProp(users, 'name');
        let autoUseContactUsers: number[] = [];
        setCustomerContacts(usersSorted.map((c) => {
            let userName = `${c.name}`;
            if (c.type === UserType.Integrator) {
                userName += ` (integrador)`;
            } else if (c.type === UserType.FinalCustomer) {
                userName += ` (cliente)`;
            } else {
                userName += ` (commbox)`;
            }
            if (c.auto_use_contact) {
                autoUseContactUsers.push(c.id);
            }
            return { value: c.id, label: userName };
        }));
        if (!initialLoad && setFieldValue) {
            setFieldValue('customer_contacts', autoUseContactUsers);
        }
    }

    async function getTechnicalAssistanceSameSeal(seal?: string) {
        if (!seal) return [];
        const where: WhereCondition<TechnicalAssistance> = [];

        where.push(['seal', '=', seal]);

        const result = await api.getList({
            where,
            order: { created_at: 'DESC' },
            relations: ['ticket_status', 'product', 'damaged_circuit_blocks'],
            limit: 5
        });

        return result;
    }

    async function fetchIntegratorTechAssisObs(integrator_id?: number | null) {
        if (integrator_id) {
            const integrator = await apiIntegrator.getById(integrator_id);
            setIntegratorTechnicalAssistanceObservation(integrator.technical_assistance_observation || '');
            setFinalCustomerTechnicalAssistanceObservation('');
        } else {
            setIntegratorTechnicalAssistanceObservation('');
            setFinalCustomerTechnicalAssistanceObservation('');
        }
    }

    async function fetchFinalCustomerTechAssisObs(final_customer_id?: number | null) {
        if (final_customer_id) {
            const finalCustomer = await apiFinalCustomer.getById(final_customer_id);
            setFinalCustomerTechnicalAssistanceObservation(finalCustomer.technical_assistance_observation || '');
        } else {
            setFinalCustomerTechnicalAssistanceObservation('');
        }
    }

    function fetchDamagedCircuitBlocks() {
        return apiDamagedCircuitBlock.getList({ order: { name: 'ASC' }, }).then((r) => {
            setDamagedCircuitBlocks(r.map((c) => { return { value: c.id, label: c.name } }));
        });
    }

    function fetchRelatedProblems() {
        return apiRelatedProblem.getList({ order: { name: 'ASC' }, }).then((r) => {
            setRelatedProblems(r.map((c) => { return { value: c.id, label: c.name } }));
        });
    }

    const initialValues: TechnicalAssistanceFormData = {
        name: item?.name || '',
        additional_item_received: item?.additional_item_received || '',
        ticket_status_id: item?.ticket_status_id || defaultTicketStatusId || null,
        product_id: item?.product_id || null,
        equipment_return: item?.equipment_return || false,
        is_recurrent_assistance: item?.is_recurrent_assistance ?? null,
        technical_assistance_id: item?.technical_assistance_id || null,
        without_invoice: item?.without_invoice || false,
        invoice_number: item?.invoice_number || '',
        observation_invoice: item?.observation_invoice || '',
        without_seal: item?.without_seal || false,
        seal: item?.seal || '',
        seal_old: item?.seal || '',
        ip: item?.ip ? item?.ip : (item?.id ? '' : '192.168.0.100'),
        tracking_post_office: item?.tracking_post_office || '',
        user_id: item?.user_id ? item?.user_id : (user ? user.id : null),
        final_customer_id: item?.final_customer_id || null,
        ticket_type_id: item?.ticket_type_id || null,
        integrator_id: item?.integrator_id || null,
        ticket_origin_id: item?.ticket_origin_id || null,
        software_version_id: item?.software_version_id || null,
        firmware_updated: item?.firmware_updated || 0,
        factory_reset: item?.factory_reset || 0,
        charge_id: item?.charge_id || null,
        ticket_cause_id: item?.ticket_cause_id || null,
        identified_problem: item?.identified_problem || '',
        necessary_procedure: item?.necessary_procedure || '',
        post_office_date: item?.post_office_date ? new Date(item?.post_office_date) : null,
        components: item?.components || [],
        damaged_circuit_blocks: item?.damaged_circuit_blocks ? item.damaged_circuit_blocks.map(c => c.id) : [],
        related_problems: item?.related_problems ? item.related_problems.map(r => r.id) : [],
        attachments: item?.attachments ? objectClone(item?.attachments) : [],
        comments: item?.comments ? objectClone(item?.comments) : [],
        cost_amount: item?.cost_amount || 0,
        cost_freight: item?.cost_freight !== undefined && item?.cost_freight !== null ? item.cost_freight : null,
        cost_freight_old: item?.cost_freight !== undefined && item?.cost_freight !== null ? item.cost_freight : null,
        cost_materials: item?.cost_materials !== undefined && item?.cost_materials !== null ? item.cost_materials : null,
        cost_materials_old: item?.cost_materials !== undefined && item?.cost_materials !== null ? item.cost_materials : null,
        created_at: item?.created_at ? moment(item.created_at).format('DD/MM/YYYY') : moment(new Date()).format('DD/MM/YYYY'),
        description_customer_problem: item?.description_customer_problem || '',
        resolution_observation: item?.resolution_observation || '',
        seals: [],
        local_pickup: item?.local_pickup || false,
        declaration_content: item?.declaration_content || false,
        service_invoice: item?.service_invoice || '',
        invoice_number_out: item?.invoice_number_out || '',
        available_for_pickup: item?.available_for_pickup || false,
        not_charged: item?.not_charged || false,
        customer_contacts: item?.customer_contacts ? item.customer_contacts.map(c => c.id) : []
    };

    function getValidationSchema() {
        const validationSchema: FormValidationSchema<TechnicalAssistanceFormData> = {
            additional_item_received: Yup.string()
                .trim()
                .required()
                .min(1)
                .max(200)
                .label('Item adicional recebido'),
            ticket_status_id: Yup.number()
                .required()
                .label('Status'),
            product_id: Yup.number()
                .required()
                .label('Produto'),
            integrator_id: Yup.number()
                .required()
                .label('Integrador/Cliente'),
            ip: Yup.string()
                .matches(/^([0-9]|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])\.([0-9]|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])\.([0-9]|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])\.([0-9]|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])$/, { excludeEmptyString: true, message: 'IP inválido' })
                .test('global-ok', 'IP inválido', (value) => value ? validateIP(value) : true)
                .label('IP')
        }

        return validationSchema;
    }

    async function hasRecentTechnicalAssistanceBySeal(seal?: string): Promise<boolean> {
        if (!seal) return false;
        const where: WhereCondition<TechnicalAssistance> = [
            ['seal', '=', seal],
            ['created_at', '>=', moment().subtract(24, 'months').format('YYYY-MM-DD HH:mm:ss')]
        ];
        if (item?.id) {
            where.push(['id', '!=', item.id]);
        }
        return (await api.count({ where })) > 0;
    }

    async function updateRecurrentAssistanceBySeal(seal: string, setFieldValue: any, isClosed = isFieldsMandatoryByTicketStatus) {
        const hasRecentAssistance = await hasRecentTechnicalAssistanceBySeal(seal);
        setHasRecentTechnicalAssistance(hasRecentAssistance);
        if (isClosed && !hasRecentAssistance) {
            await setFieldValue('is_recurrent_assistance', false);
        }
    }

    function validateIP(ip: string): boolean {
        const regex =
            /^([0-9]|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])\.([0-9]|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])\.([0-9]|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])\.([0-9]|[1-9]\d|1\d{2}|2[0-4]\d|25[0-5])$/;

        return regex.test(ip);
    }

    async function onSubmitHandler(values: TechnicalAssistanceFormData, dirty: FormDirty<TechnicalAssistanceFormData>, errors?: FormErrors<TechnicalAssistanceFormData>, submitType?: string | null) {
        window.clearTimeout(timeoutBlurSeal);
        const isDirty = Object.values(dirty).reduce((p, c) => p || c, false);

        const { seals } = values;

        if (errors) {
            for (var error of Object.values(errors).filter(e => !!e)) {
                enqueueSnackbar(error, { variant: 'error' });
            }
            return;
        }

        if (item?.id) {
            const countHasUpdated = await api.count({ where: [['id', '=', item.id], ['updated_at', '>', moment(item.updated_at).format('YYYY-MM-DD HH:mm:ss.SSS')]] });
            if (countHasUpdated > 0) {
                enqueueSnackbar("Esse registro foi alterado por outro usuário. É necessário recarregar a página.", { variant: 'error', autoHideDuration: 10000 });
                return;
            }
        }

        if (isMultiAdd) {
            let findDuplicates = seals.filter((item, index) => seals.indexOf(item) !== index);
            if (findDuplicates.length > 0) {
                enqueueSnackbar('Existem N° de Série repetidos na lista', { variant: 'error' });
                return;
            }
            for (var seal of seals) {
                if (seal !== '') {
                    //if exists a technicalAssistance with same seal and ticket_status.closed = false return error
                    const countTechnicalAssistance = await api.count({ where: [['seal', '=', seal], ['ticket_status.closed', '=', false]] });
                    if (countTechnicalAssistance > 0) {
                        enqueueSnackbar(`Já existe uma assistência aberta para o N° de Série ${seal}`, { variant: 'error' });
                        return;
                    }
                }
            }
        }

        if (values.components.length === 0 && isFieldsMandatoryByTicketStatus) {
            enqueueSnackbar('É necessário informar pelo menos um componente.', { variant: 'error' });
            return;
        }

        if (isFieldsMandatoryByTicketStatus) {
            const hasRecentAssistance = await hasRecentTechnicalAssistanceBySeal(values.seal);
            if (hasRecentAssistance && values.is_recurrent_assistance === null) {
                enqueueSnackbar('Informe se esta assistência é reincidente.', { variant: 'error' });
                return;
            }
            if (!hasRecentAssistance) {
                values.is_recurrent_assistance = false;
            }
        }

        const isStatusChanged = initialValues.ticket_status_id !== values.ticket_status_id;

        await onSubmit(values, attachmentsRemoved, isDirty, isStatusChanged, submitType);
    }

    var extraWhereUserId: WhereCondition<User> = [];
    extraWhereUserId.push(['active', '=', true]);
    extraWhereUserId.push(['type', '=', UserType.Commbox]);

    var extraWhereTechnicalAssistanceOrigim: WhereCondition<TechnicalAssistance> = [];
    if (item?.id) {
        extraWhereTechnicalAssistanceOrigim.push(['id', '!=', item.id]);
    }

    function addAttachment(attachments: AttachmentTechnicalAssistanceRow[], setFieldValue: any, name: string, file: File) {
        if (attachments.length < 10) {
            attachments.push({ id: 0, description: '', name, file });
            setFieldValue('attachments', attachments);
        }
    }

    function removeAttachment(idx: number, attachments: AttachmentTechnicalAssistanceRow[], setFieldValue: any) {
        let attachment = attachments.find((o, i) => i === idx);
        setFieldValue('attachments', attachments.filter((o, i) => i !== idx));
        if (attachment && attachment.id > 0) {
            attachmentsRemoved.push(attachment);
            setAttachmentsRemoved(attachmentsRemoved);
        }
    }

    async function downloadAttachment(fileName: string) {
        let dir = `TechnicalAssistance/${item?.id}/${fileName}`;
        await downloadBlob(dir, fileName);
    }

    function openAttachmentDescriptionDialog(idx: number) {
        setAttachmentsEditingIdx(idx);
        setAttachmentDescriptionDialogOpen(true);
    }

    function closeAttachmentDescriptionDialog() {
        setAttachmentsEditingIdx(null);
        setAttachmentDescriptionDialogOpen(false);
    }

    function submitAttachmentDescriptionDialog(setFieldValue: any, attachments: AttachmentTechnicalAssistanceRow[], description: string, idx: number | null) {
        if (idx === null) return;
        attachments[idx].description = description;
        attachments[idx].edited = true;
        setFieldValue('attachments', attachments);
        setAttachmentDescriptionDialogOpen(false);
        setAttachmentsEditingIdx(null);
    }

    function openCommentDialog() {
        setCommentDialogOpen(true);
    }

    function closeCommentDialog() {
        setCommentDialogOpen(false);
    }

    function submitCommentDialog(setFieldValue: any, comments: CommentTechnicalAssistance[], comment: string, is_public: boolean) {
        if (comment.trim() !== '') {
            let newComment = { id: 0, comment, user_id: user?.id || 0, is_public };
            comments.push(newComment);
            eventEmmiter.emit('newComment', newComment);
            setFieldValue('comments', comments);
        }
        setCommentDialogOpen(false);
    }

    function setChanges(changed: boolean) {
        setDirty(changed);
        if (onChange)
            onChange(changed);
    }

    function customLabelCharge(charge: Charge) {
        let chargeSufix = '';
        if (charge.cost !== null) {
            chargeSufix = ' - ' + formatCurrency(charge.cost);
        }
        return `${charge.name}${chargeSufix}`;
    }

    async function calculateCostMaterials(setFieldValue: any, values: TechnicalAssistanceFormData) {
        let cost_materials = 0;
        for (const component of values.components) {
            if (component.sale_value && component.amount && component.amount > 0) {
                cost_materials += component.sale_value * component.amount;
            }
        }
        await setFieldValue('cost_materials', cost_materials || null);
        await calculateCostAmount(setFieldValue, values.charge_id, cost_materials, values.cost_freight);
    }

    async function calculateCostAmount(setFieldValue: any, charge_id: number | null, cost_materials: number | null, cost_freight: number | null) {
        let chargeAmount: number | null = null;
        if (charge_id) {
            const charge = await apiCharge.getById(charge_id);
            chargeAmount = charge.cost;
        }
        if (cost_materials && cost_materials < 50) {
            cost_materials = 0;
        }
        let newCostAmount: number = (chargeAmount || 0) + (cost_materials || 0) + (cost_freight || 0);
        await setFieldValue('cost_amount', newCostAmount);
    }

    async function setMandatoryFieldsByStatus(ticket_status_id?: number | null, seal?: string, setFieldValue?: any) {
        let isMandatory = false;
        if (ticket_status_id) {
            const ticketStatus = await apiTicketStatus.getById(ticket_status_id);
            isMandatory = ticketStatus.closed;
        }
        setIsFieldsMandatoryByTicketStatus(isMandatory);
        if (isMandatory && setFieldValue) {
            await updateRecurrentAssistanceBySeal(seal || '', setFieldValue, true);
        }
    }

    function getTechnicalAssistanceRow(row: TechnicalAssistance): TechnicalAssistanceRow {
        let nowYear = moment().format('YYYY');
        let rowYear = moment(row.created_at).format('YYYY');
        let created_at: React.ReactNode = moment(row.created_at).format('DD/MM HH') + 'h';
        if (nowYear !== rowYear) {
            created_at = <span style={{ color: '#DF7B00' }}>{moment(row.created_at).format('DD/MM/YYYY')}</span>;
        }

        return {
            id: row.id,
            name: <>{item?.id === row.id ? <span style={{ color: 'green', fontWeight: 'bold' }}>EM EDIÇÃO - </span> : ''}<a className='visitedBlue' target="_blank" href={`#/system/technicalAssistance/${row.id}/general`}>{row.name}</a></>,
            status: row.ticket_status?.name ? <span style={{ display: 'flex', alignItems: 'center' }}><TicketStatusIcon icon={row.ticket_status.icon} style={{ marginRight: 5 }} />{row.ticket_status.name}</span> : '',
            product_name: row.product?.name || '',
            created_date: created_at,
            cost_amount: row.cost_amount !== null ? formatCurrency(row.cost_amount) : '',
            last_update_status: row.last_update_status ? moment(row.last_update_status).format('DD/MM HH') + 'h' : '',
            identified_problem: row.identified_problem,
            necessary_procedure: row.necessary_procedure,
            circuits: row.damaged_circuit_blocks?.map(d => d.name).join(', ') || ''
        }
    }

    function onCancelTechAssisComponentFormDialog() {
        setTechAssisComponentDialogOpen(false);
    }

    async function onSubmitTechAssisComponentFormDialog(data: TechAssisComponentFormDialogData, setFieldValue: any, values: TechnicalAssistanceFormData) {
        if (data.component_id) {
            const component: Component = await apiComponent.getById(data.component_id);
            const compositeProductsClone = objectClone(values.components);
            const existingComponent = compositeProductsClone.find(c => c.component_id === data.component_id);
            if (existingComponent) {
                existingComponent.amount = data.amount;
                existingComponent.purchase_value = data.purchase_value;
                existingComponent.sale_value = data.sale_value;
            } else {
                compositeProductsClone.push({ component: component, component_id: data.component_id, amount: data.amount, technical_assistance_id: item?.id || 0, purchase_value: data.purchase_value, sale_value: data.sale_value });
            }

            setTechAssisComponentDialogOpen(false);
            await setFieldValue('components', compositeProductsClone);
            await calculateCostMaterials(setFieldValue, { ...values, components: compositeProductsClone });
        }
    }

    async function openCompositeProductFormDialog() {
        setTechAssisComponentDialogOpen(true);
    }

    async function removerTechAssisComponent(componentId: number, setFieldValue: any, values: TechnicalAssistanceFormData) {
        const componentsClone = objectClone(values.components);
        const componentsNew = componentsClone.filter(r => r.component_id !== componentId);

        await setFieldValue('components', componentsNew);
        await calculateCostMaterials(setFieldValue, { ...values, components: componentsNew });
    }

    // [['type', '=', SoftwareVersionType.Firmware], ['products.product_id', '=', values.product_id]]

    return (
        <Panel
            panelHeaderTitle={title}
            panelFooterButtons={<FormFooterAllButtons removeSaveButton={isMultiAdd} onUndo={onUndo} form="tech_form" onBack={onCancel} isDirty={isDirty} enableSaveAndCreateNew={true} />}
        >
            <PanelBodyFormContent style={{ overflow: 'auto' }}>
                <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Form<TechnicalAssistanceFormData>
                            initialValues={initialValues}
                            validationSchema={getValidationSchema()}
                            onSubmit={onSubmitHandler}
                            onChange={setChanges}
                            style={{ overflow: 'auto' }}
                            id="tech_form"
                        >
                            {({ isSubmitting, values, errors, handleChange, dirty, setFieldValue, handleChangeNumber, handleChangeFloat }) => {
                                return (
                                    <>
                                        <Box marginTop={3}>
                                            <Typography variant='subtitle2' className="subtitleGradientBackgorund">Assistência Técnica</Typography>
                                        </Box>
                                        <Divider style={{ marginBottom: 4 }} />
                                        <Grid spacing={1} container>
                                            {isMultiAdd ? <Grid alignContent={'center'} alignItems={'center'} display={'flex'} item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                <ListTextField
                                                    value={values.seals || []}
                                                    onChange={async (v) => {
                                                        const uniqueSeals = Array.from(new Set(v.map(s => s.trim())));
                                                        await setFieldValue('seals', uniqueSeals.slice(0, 50));
                                                        if (v.length > 50) {
                                                            enqueueSnackbar('Limite de 50 N° de Série', { variant: 'error' });
                                                        }
                                                    }}
                                                    name="seals"
                                                    label="Lista de N° de Série"
                                                    rows={6}
                                                    helperText='Um N° de Série por linha'
                                                    required
                                                />
                                            </Grid>
                                                : <Grid alignContent={'center'} alignItems={'center'} display={'flex'} item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                    <TextField
                                                        name="seal"
                                                        label={'N° de Série'}
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        autoFocus={!item?.id}
                                                        required={(values.without_seal === false)}
                                                        disabled={(values.without_seal === true)}
                                                        inputProps={{ maxLength: 200 }}
                                                        style={{ flex: 1 }}
                                                        value={values.seal || ''}
                                                        onChange={handleChange}
                                                        onFocus={() => {
                                                            if (values.seal !== values.seal_old) {
                                                                setFieldValue('seal_old', values.seal);
                                                            }
                                                        }}
                                                        onBlur={async () => {
                                                            if (values.seal !== values.seal_old) {
                                                                window.clearTimeout(timeoutBlurSeal);
                                                                timeoutBlurSeal = window.setTimeout(async () => {
                                                                    if (values.seal) {
                                                                        await loadLastTechAssisBySeal(values.seal, setFieldValue, setProduction, setDisableFieldIp, item);
                                                                        const techSameSealList = await getTechnicalAssistanceSameSeal(values.seal);
                                                                        setTechnicalAssistanceSameSeal(techSameSealList);
                                                                        await updateRecurrentAssistanceBySeal(values.seal, setFieldValue);
                                                                    } else {
                                                                        setTechnicalAssistanceSameSeal([]);
                                                                        setProduction(null);
                                                                        await updateRecurrentAssistanceBySeal('', setFieldValue);
                                                                    }
                                                                }, 500);
                                                            }
                                                        }}
                                                        error={!!errors.seal && !!dirty.seal}
                                                        helperText={!!errors.seal && !!dirty.seal && errors.seal}
                                                    />
                                                    {!item?.id ? <Tooltip placement="top" title={'Adicionar assistências em lote'}>
                                                        <IconButton
                                                            size='small'
                                                            color="inherit"
                                                            aria-label={'Adicionar assistências em lote'}
                                                            onClick={async () => {
                                                                await setFieldValue('without_seal', false);
                                                                await setFieldValue('seal', '');
                                                                await setFieldValue('seal_old', '');
                                                                await setFieldValue('technical_assistance_id', null);
                                                                await setFieldValue('equipment_return', false);
                                                                // remover anexos e comentários sem id
                                                                await setFieldValue('attachments', values.attachments.filter(a => a.id !== 0));
                                                                await setFieldValue('comments', values.comments.filter(a => a.id !== 0));
                                                                setIsMultiAdd(true);
                                                            }}
                                                        >
                                                            <LibraryAddIcon />
                                                        </IconButton>
                                                    </Tooltip> : null}
                                                    <FormControlLabel
                                                        style={{ margin: 0 }}
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                name='without_seal'
                                                                onChange={async (_, v) => {
                                                                    await setFieldValue('without_seal', v || false);
                                                                    if (v === true) {
                                                                        await setFieldValue('seal', '');
                                                                    }
                                                                }}
                                                                checked={values.without_seal === true}
                                                            />
                                                        }
                                                        label="Sem N° de Série"
                                                    />
                                                </Grid>}
                                            <Grid alignContent={'center'} alignItems={'center'} display={'flex'} item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                <TextField
                                                    name="invoice_number"
                                                    label={'Nota fiscal de entrada'}
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    style={{ flex: 1 }}
                                                    required={(values.without_invoice === false)}
                                                    disabled={(values.without_invoice === true)}
                                                    inputProps={{ maxLength: 200 }}
                                                    value={values.invoice_number || ''}
                                                    onChange={e => {
                                                        const re = /^[0-9\b]+$/;
                                                        if (e.target.value === '' || re.test(e.target.value)) {
                                                            handleChange(e);
                                                        }
                                                    }}
                                                    error={!!errors.invoice_number && !!dirty.invoice_number}
                                                    helperText={!!errors.invoice_number && !!dirty.invoice_number && errors.invoice_number}
                                                />
                                                <FormControlLabel
                                                    style={{ margin: 0 }}
                                                    control={
                                                        <Checkbox
                                                            name='without_invoice'
                                                            size="small"
                                                            onChange={async (_, v) => {
                                                                await setFieldValue('without_invoice', v || false);
                                                                if (v === false) {
                                                                    await setFieldValue('observation_invoice', '');
                                                                } else {
                                                                    await setFieldValue('invoice_number', '');
                                                                }
                                                            }}
                                                            checked={values.without_invoice === true}
                                                        />
                                                    }
                                                    label="Sem NF entrada"
                                                />
                                            </Grid>
                                        </Grid>
                                        {
                                            values.without_invoice === true ?
                                                <Grid spacing={1} container>
                                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                                        <TextField
                                                            name="observation_invoice"
                                                            label={"Observaçõe NF"}
                                                            margin="dense"
                                                            variant="outlined"
                                                            fullWidth
                                                            multiline
                                                            rows={3}
                                                            value={values.observation_invoice}
                                                            onChange={handleChange}
                                                            error={!!errors.observation_invoice && !!dirty.observation_invoice}
                                                            helperText={!!errors.observation_invoice && !!dirty.observation_invoice && errors.observation_invoice}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                : null
                                        }
                                        {item?.id ?
                                            <Grid spacing={1} container>
                                                <Grid item xs={12} sm={12} md={12} lg={9} xl={9}>
                                                    <TextField
                                                        name="name"
                                                        label='Assunto'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        disabled
                                                        value={values.name}
                                                        onChange={handleChange}
                                                        error={!!errors.name && !!dirty.name}
                                                        helperText={!!errors.name && !!dirty.name && errors.name}
                                                    />
                                                </Grid>
                                                {item?.created_by_user ?
                                                    <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                        <TextField
                                                            name="created_by_user"
                                                            label='Criado por'
                                                            margin="dense"
                                                            variant="outlined"
                                                            fullWidth
                                                            disabled
                                                            value={item.created_by_user.name}
                                                        />
                                                    </Grid>
                                                    : null}
                                            </Grid> : null}
                                        <Grid spacing={1} container>
                                            {/*<Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                <SearchEntity<TicketType, 'id', 'name'>
                                                    name="ticket_type_id"
                                                    entity="ticketType"
                                                    valueField='id'
                                                    labelField="name"
                                                    label="Tipo"
                                                    value={values.ticket_type_id || null}
                                                    onChangeHandle={(_, v) => setFieldValue('ticket_type_id', v)}
                                                    required
                                                    extraWhere={[['use_in_technical_assistance', '=', true]]}
                                                />
                                            </Grid>*/}
                                            <Grid item xs={12} sm={12} md={12} lg={4} xl={4} style={{ display: 'flex', alignItems: 'center' }}>
                                                <SearchEntity<Integrator, 'id', 'name_with_cnpj_and_erp'>
                                                    name="integrator_id"
                                                    entity="integrator"
                                                    valueField='id'
                                                    labelField="name_with_cnpj_and_erp"
                                                    label="Integrador / Cliente"
                                                    style={{ flex: 1 }}
                                                    value={values.integrator_id || null}
                                                    onChangeHandle={async (_, v) => {
                                                        await setFieldValue('integrator_id', v);
                                                        await setFieldValue('customer_contacts', []);
                                                        await setFieldValue('final_customer_id', null);
                                                        await fetchIntegratorTechAssisObs(v);
                                                        await loadCustomerContacts(values.final_customer_id, v, setFieldValue);
                                                    }}
                                                    required
                                                    key={`integrator_techassis_${productKey}`}
                                                />
                                                <Tooltip placement="top" title={'Abrir em nova guia cadastro de integrador'}>
                                                    <IconButton
                                                        size='small'
                                                        color="inherit"
                                                        aria-label={'Abrir em nova guia cadastro de integrador'}
                                                        onClick={() => {
                                                            if (values.integrator_id) {
                                                                window.open(`#/system/administration/integrators/${values.integrator_id}`, "_blank");
                                                            } else {
                                                                window.open(`#/system/administration/integrators/add`);
                                                            }
                                                        }}
                                                    >
                                                        <OpenInNewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip placement="top" title={!integratorTechnicalAssistanceObservation ? 'Não há observações de assistência deste integrador' : 'Observações de chamado do integrador'}>
                                                    <span>
                                                        <IconButton
                                                            size='small'
                                                            color="inherit"
                                                            aria-label={!integratorTechnicalAssistanceObservation ? 'Não há observações de assistência deste integrador' : 'Observações de chamado do integrador'}
                                                            onClick={() => setIntegratorTechnicalAssistanceObservationDialogOpen(true)}
                                                            disabled={!integratorTechnicalAssistanceObservation}
                                                        >
                                                            <InfoIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={4} xl={4} style={{ display: 'flex', alignItems: 'center' }}>
                                                <SearchEntity<FinalCustomer, 'id', 'name'>
                                                    name="final_customer_id"
                                                    entity="finalCustomer"
                                                    valueField='id'
                                                    labelField="name"
                                                    isClearable
                                                    label="Cliente final"
                                                    style={{ flex: 1 }}
                                                    value={values.final_customer_id || null}
                                                    onChangeHandle={async (_, v) => {
                                                        await setFieldValue('final_customer_id', v);
                                                        await setFieldValue('customer_contacts', []);
                                                        await fetchFinalCustomerTechAssisObs(v);
                                                        await loadCustomerContacts(v, values.integrator_id, setFieldValue);
                                                    }}
                                                    extraWhere={[['integrator_id', '=', values.integrator_id]]}
                                                    isDisabled={!values.integrator_id}
                                                    key={`final_customer_${values.integrator_id}`}
                                                />
                                                <Tooltip placement="top" title={'Abrir em nova guia cadastro de cliente final'}>
                                                    <IconButton
                                                        size='small'
                                                        color="inherit"
                                                        aria-label={'Abrir em nova guia cadastro de cliente final'}
                                                        onClick={() => {
                                                            if (values.final_customer_id) {
                                                                window.open(`#/system/administration/finalCustomers/${values.final_customer_id}`);
                                                            } else {
                                                                window.open(`#/system/administration/finalCustomers/add`);
                                                            }
                                                        }}
                                                    >
                                                        <OpenInNewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip placement="top" title={!finalCustomerTechnicalAssistanceObservation ? 'Não há observações de chamado deste cliente final' : 'Observações de chamado do cliente final'}>
                                                    <span>
                                                        <IconButton
                                                            size='small'
                                                            color="inherit"
                                                            aria-label={!finalCustomerTechnicalAssistanceObservation ? 'Não há observações de chamado deste cliente final' : 'Observações de chamado do cliente final'}
                                                            onClick={() => setFinalCustomerTechnicalAssistanceObservationDialogOpen(true)}
                                                            disabled={!finalCustomerTechnicalAssistanceObservation}
                                                        >
                                                            <InfoIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
                                                <SearchEntity<TicketOrigin, 'id', 'name'>
                                                    name="ticket_origin_id"
                                                    entity="ticketOrigin"
                                                    valueField='id'
                                                    labelField="name"
                                                    label="Origem"
                                                    value={values.ticket_origin_id || null}
                                                    onChangeHandle={(_, v) => setFieldValue('ticket_origin_id', v)}
                                                    isClearable
                                                    extraWhere={[['use_in_technical_assistance', '=', true]]}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid spacing={1} container>
                                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                                <MultiSelect<number>
                                                    name='customer_contacts'
                                                    label={'Contatos do Cliente/Integrador'}
                                                    onChange={(com) => setFieldValue('customer_contacts', com)}
                                                    options={customerContacts}
                                                    values={values.customer_contacts}
                                                    required
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid spacing={1} container>
                                            <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                                                <SearchEntity<TicketStatus, 'id', 'name'>
                                                    name="ticket_status_id"
                                                    entity="ticketStatus"
                                                    valueField='id'
                                                    labelField="name"
                                                    label="Status"
                                                    value={values.ticket_status_id || null}
                                                    onChangeHandle={async (_, v) => {
                                                        await setFieldValue('ticket_status_id', v);
                                                        await setMandatoryFieldsByStatus(v, values.seal, setFieldValue);
                                                    }}
                                                    required
                                                    extraWhere={[['use_in_technical_assistance', '=', true]]}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                                                <TextField
                                                    name="last_update_status"
                                                    label='Última atualização status'
                                                    margin="dense"
                                                    variant="outlined"
                                                    disabled
                                                    value={item?.last_update_status ? moment(item.last_update_status).format('DD/MM/YYYY HH:mm:ss') : ''}
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                <SearchEntity<User, 'id', 'name'>
                                                    name="user_id"
                                                    entity="user"
                                                    valueField='id'
                                                    labelField="name"
                                                    label="Atribuido para"
                                                    value={values.user_id ? values.user_id : (user ? user.id : null)}
                                                    onChangeHandle={(_, v) => setFieldValue('user_id', v)}
                                                    extraWhere={extraWhereUserId}
                                                    required
                                                    normalizeSearch
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                <SearchEntity<Product, 'id', 'name'>
                                                    name="product_id"
                                                    entity="product"
                                                    valueField='id'
                                                    labelField="name"
                                                    label="Produto"
                                                    value={values.product_id || null}
                                                    onChangeHandle={async (_, v) => {
                                                        await setFieldValue('product_id', v);
                                                        await setFieldValue('software_version_id', null);
                                                        let isHardwareIp = await getProductIsHardwareIp(v);
                                                        if (isHardwareIp === false) {
                                                            setDisableFieldIp(true);
                                                            await setFieldValue('ip', '');
                                                        } else {
                                                            setDisableFieldIp(false);
                                                        }
                                                    }}
                                                    required
                                                    inputStyle={{
                                                        backgroundColor: theme.palette.background.default === '#202326'
                                                            ? theme.palette.background.paper
                                                            : theme.palette.mode === 'dark' ? '#a3bbd2' : 'aliceblue',
                                                        ...(theme.palette.background.default === '#202326' ? { color: theme.palette.text.primary } : {})
                                                    }}
                                                    //extraWhere={[['use_in_technical_assistance', '=', true], ['final_customers.final_customer_id', '=', values.final_customer_id]]}
                                                    extraWhere={[['use_in_technical_assistance', '=', true]]}
                                                    key={`product_techassis_${productKey}`}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                                                <TextField
                                                    name="created_at"
                                                    label='Data inicio'
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    disabled
                                                    value={values.created_at}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid spacing={1} container>
                                            <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                <SelectSearch<boolean>
                                                    name='equipment_return'
                                                    label='Retorno de equipamento'
                                                    options={[
                                                        { label: 'Sim', value: true },
                                                        { label: 'Não', value: false },
                                                    ]}
                                                    value={values.equipment_return}
                                                    onChangeHandle={(_, v) => {
                                                        setFieldValue('equipment_return', v || false)
                                                    }}
                                                    margin='dense'
                                                    fullWidth
                                                    required
                                                    isDisabled
                                                //helperText={<a href="#">Clique aqui para exibir assistencias do mesmo N° série</a>}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={9} xl={9}>
                                                <SearchEntity<TechnicalAssistance, 'id', 'name'>
                                                    name="technical_assistance_id"
                                                    entity="technicalAssistance"
                                                    valueField='id'
                                                    labelField="name"
                                                    label="Assistência Técnica Origem"
                                                    value={values.technical_assistance_id || null}
                                                    onChangeHandle={(_, v) => setFieldValue('technical_assistance_id', v)}
                                                    extraWhere={extraWhereTechnicalAssistanceOrigim}
                                                    isClearable
                                                    isDisabled={isMultiAdd}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid spacing={1} container>
                                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                                <TextField
                                                    name="additional_item_received"
                                                    label='Item adicional recebido'
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    required
                                                    inputProps={{ maxLength: 200 }}
                                                    value={values.additional_item_received}
                                                    onChange={handleChange}
                                                    error={!!errors.additional_item_received && !!dirty.additional_item_received}
                                                    helperText={!!errors.additional_item_received && !!dirty.additional_item_received && errors.additional_item_received}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid spacing={1} container>
                                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                                <TextField
                                                    name="description_customer_problem"
                                                    label='Descrição do problema segundo cliente'
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    inputProps={{ maxLength: 200 }}
                                                    value={values.description_customer_problem}
                                                    onChange={handleChange}
                                                    error={!!errors.description_customer_problem && !!dirty.description_customer_problem}
                                                    helperText={!!errors.description_customer_problem && !!dirty.description_customer_problem && errors.description_customer_problem}
                                                />
                                            </Grid>
                                        </Grid>
                                        {isMultiAdd === false ? <>
                                            <Box marginTop={3}>
                                                <Typography variant='subtitle2' className="subtitleGradientBackgorund">Informações da Produção</Typography>
                                            </Box>
                                            <Divider style={{ marginBottom: 4 }} />
                                            <Grid spacing={1} container>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3} style={{ display: 'flex', alignItems: 'center' }}>
                                                    {production?.id ? <a target='_blank' className='visitedBlue' href={`#/system/production/${production.id}/general`}>Abrir Produção em nova guia</a> : null}
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_1"
                                                        label='Criado por'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production?.user?.name || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_2"
                                                        label='Data entrada'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production?.entry_at ? moment(production.entry_at).format('DD/MM/YYYY') : ''}
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_3"
                                                        label='Firmware'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production?.software_version?.name || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid spacing={1} container>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_4"
                                                        label='Revisão PCI'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production?.product_review_1?.name || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_5"
                                                        label='Lote'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production?.product_batch_1?.name || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_6"
                                                        label='Revisão Superior'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production?.product_review_2?.name || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_7"
                                                        label='Lote Superior'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production?.product_batch_2?.name || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid spacing={1} container>
                                                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                                    <TextField
                                                        name="production_field_8"
                                                        label='Observações'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        rows={3}
                                                        multiline
                                                        value={production?.observation || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid spacing={1} container>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_9"
                                                        label='MAC Address'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production?.mac_address || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_10"
                                                        label='Data saída'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production?.left_at ? moment(production.left_at).format('DD/MM/YYYY') : ''}
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_11"
                                                        label='N° de pedido'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production?.order_number || ''}
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="production_field_12"
                                                        label='Houve não conformidades?'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={production ? (production.non_conformities && production.non_conformities?.length > 0 ? 'Sim' : 'Não') : ''}
                                                        disabled
                                                        style={{
                                                            ...(production && production.non_conformities && production.non_conformities?.length > 0 ? { backgroundColor: 'rgba(255,0,0,0.1)' } : {})
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Box marginTop={3}>
                                                <Typography variant='subtitle2' className="subtitleGradientBackgorund">Últimas 5 assistências para esse número de série</Typography>
                                            </Box>
                                            <Divider style={{ marginBottom: 4 }} />
                                            <TableListPsim<TechnicalAssistanceRow>
                                                columns={[
                                                    { field: 'id', width: 17, visible: false },
                                                    { field: 'created_date', width: 70, value: 'Cadastro', alignContent: 'center' },
                                                    { field: 'name', value: 'Assunto', alignContent: 'left' },
                                                    { field: 'identified_problem', value: 'Prob. Ident.', alignContent: 'left' },
                                                    { field: 'necessary_procedure', value: 'Proc. Necessário', alignContent: 'left' },
                                                    { field: 'circuits', value: 'Blocos Avariados', alignContent: 'left' },
                                                    { field: 'status', value: 'Status', alignContent: 'left', width: 195, verticalAlign: 'middle' },
                                                    { field: 'last_update_status', value: 'Desde', alignContent: 'center', width: 70 },
                                                    { field: 'cost_amount', value: 'Valor final', alignContent: 'right', width: 75 }
                                                ]}
                                                rows={technicalAssistanceSameSeal.map(getTechnicalAssistanceRow)}
                                                noPadding
                                                emptyRowsLabel={values.seal ? 'Nenhuma assistência cadastrada para esse número de série' : 'Nenhum número de série inserido'}
                                            />
                                        </> : null}
                                        <Box marginTop={3}>
                                            <Typography variant='subtitle2' className="subtitleGradientBackgorund">Resolução</Typography>
                                        </Box>
                                        <Divider style={{ marginBottom: 4 }} />
                                        {/*<SearchEntity<TicketCause, 'id', 'name'>
                                            name="ticket_cause_id"
                                            entity="ticketCause"
                                            valueField='id'
                                            labelField="name"
                                            label="Causa"
                                            value={values.ticket_cause_id || null}
                                            onChangeHandle={(_, v) => setFieldValue('ticket_cause_id', v)}
                                            extraWhere={[['use_in_technical_assistance', '=', true]]}
                                            isClearable
                                        />*/}
                                        <TextField
                                            name="identified_problem"
                                            label='Problema identificado'
                                            margin="dense"
                                            variant="outlined"
                                            fullWidth
                                            inputProps={{ maxLength: 200 }}
                                            value={values.identified_problem}
                                            onChange={handleChange}
                                            error={!!errors.identified_problem && !!dirty.identified_problem}
                                            helperText={!!errors.identified_problem && !!dirty.identified_problem && errors.identified_problem}
                                            required={isFieldsMandatoryByTicketStatus}
                                        />
                                        <TextField
                                            name="necessary_procedure"
                                            label='Procedimento necessário'
                                            margin="dense"
                                            variant="outlined"
                                            fullWidth
                                            inputProps={{ maxLength: 200 }}
                                            value={values.necessary_procedure}
                                            onChange={handleChange}
                                            error={!!errors.necessary_procedure && !!dirty.necessary_procedure}
                                            helperText={!!errors.necessary_procedure && !!dirty.necessary_procedure && errors.necessary_procedure}
                                            required={isFieldsMandatoryByTicketStatus}
                                        />
                                        <Grid spacing={1} container>
                                            <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
                                                <SelectSearch<boolean>
                                                    name='is_recurrent_assistance'
                                                    label='Reincidência de assistência'
                                                    options={[
                                                        { label: 'Sim', value: true },
                                                        { label: 'Não', value: false }
                                                    ]}
                                                    value={values.is_recurrent_assistance}
                                                    onChangeHandle={(_, v) => setFieldValue('is_recurrent_assistance', v)}
                                                    margin='dense'
                                                    fullWidth
                                                    required={isFieldsMandatoryByTicketStatus && hasRecentTechnicalAssistance}
                                                    isDisabled={!isFieldsMandatoryByTicketStatus || !hasRecentTechnicalAssistance}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
                                                {/* <MultiSelect<number>
                                                    name='components'
                                                    label={'Componentes'}
                                                    onChange={(com) => setFieldValue('components', com)}
                                                    options={components}
                                                    values={values.components}
                                                /> */}
                                                <MultiSelect<number>
                                                    name='related_problems'
                                                    label={'Problemas relacionados'}
                                                    onChange={(com) => setFieldValue('related_problems', com)}
                                                    options={relatedProblems}
                                                    values={values.related_problems}
                                                    required={isFieldsMandatoryByTicketStatus}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
                                                <MultiSelect<number>
                                                    name='damaged_circuit_blocks'
                                                    label={'Blocos de circuitos avariados'}
                                                    onChange={(com) => setFieldValue('damaged_circuit_blocks', com)}
                                                    options={damagedCircuitBlocks}
                                                    values={values.damaged_circuit_blocks}
                                                    required={isFieldsMandatoryByTicketStatus}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid spacing={1} container>
                                            <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                <SelectSearch<number>
                                                    name='firmware_updated'
                                                    label='Firmware atualizado'
                                                    options={[
                                                        { label: 'N/A', value: 0 },
                                                        { label: 'Sim', value: 1 },
                                                        { label: 'Não', value: 2 },
                                                    ]}
                                                    value={values.firmware_updated}
                                                    onChangeHandle={async (_, v) => {
                                                        await setFieldValue('firmware_updated', v || 0);
                                                        await setFieldValue('software_version_id', null);
                                                    }}
                                                    margin='dense'
                                                    fullWidth
                                                    required
                                                />
                                            </Grid>
                                            {
                                                values.firmware_updated === 1 ?
                                                    <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                        <SearchEntity<Software, 'id', 'name'>
                                                            name="software_version_id"
                                                            entity="softwareVersion"
                                                            valueField='id'
                                                            labelField="name"
                                                            label="Firmware"
                                                            value={values.software_version_id || null}
                                                            onChangeHandle={(_, v) => setFieldValue('software_version_id', v)}
                                                            key={`software_version_${values.product_id}`}
                                                            extraWhere={[['type', '=', SoftwareVersionType.Firmware], ['products.id', '=', values.product_id]]}
                                                            isClearable
                                                            isDisabled={!values.product_id}
                                                        />
                                                    </Grid> : null
                                            }
                                            <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                <SelectSearch<number>
                                                    name='factory_reset'
                                                    label='Reset de fábrica'
                                                    options={[
                                                        { label: 'N/A', value: 0 },
                                                        { label: 'Sim', value: 1 },
                                                        { label: 'Não', value: 2 },
                                                    ]}
                                                    value={values.factory_reset}
                                                    onChangeHandle={(_, v) => {
                                                        setFieldValue('factory_reset', v || 0);
                                                    }}
                                                    margin='dense'
                                                    fullWidth
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                <TextField
                                                    name="ip"
                                                    label='IP'
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    inputProps={{ maxLength: 50 }}
                                                    value={values.ip}
                                                    disabled={disableFieldIp}
                                                    onChange={handleChange}
                                                    error={!!errors.ip && !!dirty.ip}
                                                    helperText={!!errors.ip && !!dirty.ip && errors.ip}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                                <TextField
                                                    name="resolution_observation"
                                                    label={"Obs / Complemento"}
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    value={values.resolution_observation}
                                                    onChange={handleChange}
                                                    error={!!errors.resolution_observation && !!dirty.resolution_observation}
                                                    helperText={!!errors.resolution_observation && !!dirty.resolution_observation && errors.resolution_observation}
                                                />
                                            </Grid>
                                        </Grid>
                                        {/* componentes vai vir pra cá */}
                                        <Box marginTop={3} marginBottom={0} borderBottom={`1px solid ${theme.palette.divider}`} display="flex" bgcolor={theme.palette.background.default}>
                                            <Typography variant='subtitle2' style={{ flex: 1 }} className="subtitleGradientBackgorund">Componentes</Typography>
                                        </Box>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell className={classes.headerCell} key={`header_cell_1`} style={{ width: 'auto' }}>
                                                        Componente
                                                    </TableCell>
                                                    <TableCell className={classes.headerCell} key={`header_cell_2`} style={{ width: '100px' }}>
                                                        Quantidade
                                                    </TableCell>
                                                    <TableCell className={classes.headerCell} key={`header_cell_3`} style={{ width: '150px' }}>
                                                        Valor de venda
                                                    </TableCell>
                                                    <TableCell className={classes.headerCell} key={`header_cell_4`} style={{ width: '150px' }}>
                                                        Valor de compra
                                                    </TableCell>
                                                    <TableCell className={classes.headerCell} style={{ width: 60 }} key={`header_cell_5`}>
                                                        <Tooltip placement="top" title={'Adicionar componente'}>
                                                            <IconButton
                                                                size='small'
                                                                color="inherit"
                                                                aria-label={'Adicionar componente'}
                                                                onClick={() => openCompositeProductFormDialog()}
                                                                sx={{ padding: 0 }}
                                                            >
                                                                <AddIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Array.isArray(values.components) && values.components.length > 0 ? values.components.map((row, r) => (
                                                    <TableRow key={`body_row_${r}`}>
                                                        <TableCell key={`body_cell_${r}_1`} className={classes.bodyCell} style={{ textAlign: 'left' }}>{(row.component?.code ? <>{row.component?.code} - </> : '')}{row.component?.name}</TableCell>
                                                        <TableCell key={`body_cell_${r}_2`} className={classes.bodyCell} style={{ textAlign: 'center' }}>{row.amount}</TableCell>
                                                        <TableCell key={`body_cell_${r}_3`} className={classes.bodyCell} style={{ textAlign: 'right' }}>{row.sale_value ? formatCurrency(row.sale_value) : ''}</TableCell>
                                                        <TableCell key={`body_cell_${r}_4`} className={classes.bodyCell} style={{ textAlign: 'right' }}>{row.purchase_value ? formatCurrency(row.purchase_value) : ''}</TableCell>
                                                        <TableCell key={`body_cell_${r}_5`} className={classes.bodyCell} style={{ textAlign: 'center', width: 30 }}>
                                                            <Tooltip placement="top" title={'Remover componente'}>
                                                                <IconButton
                                                                    size='small'
                                                                    color="inherit"
                                                                    aria-label={'Remover componente'}
                                                                    onClick={async () => {
                                                                        confirmation(undefined, {
                                                                            title: 'Confirma a exclusão do componente?',
                                                                            okLabel: 'Sim',
                                                                            cancelLabel: 'Não',
                                                                            onProceed: async () => {
                                                                                await removerTechAssisComponent(row.component_id, setFieldValue, values);
                                                                            }
                                                                        });
                                                                    }}
                                                                    sx={{ padding: 0 }}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                )) : <TableRow key={`body_row_1`}>
                                                    <TableCell colSpan={5} key={`body_cell_1_1`} className={classes.bodyCell} style={{ textAlign: 'center' }}>Nenhum componente adicionado.</TableCell>
                                                </TableRow>}
                                            </TableBody>
                                        </Table>
                                        <Box marginTop={3}>
                                            <Typography variant='subtitle2' className="subtitleGradientBackgorund">Custo</Typography>
                                        </Box>
                                        <Divider style={{ marginBottom: 4 }} />
                                        <Grid spacing={1} container>
                                            <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                <SearchEntity<Charge, 'id', 'name'>
                                                    name="charge_id"
                                                    entity="charge"
                                                    valueField='id'
                                                    labelField="name"
                                                    label="Cobrança"
                                                    value={values.charge_id || null}
                                                    onChangeHandle={async (_, v) => {
                                                        await setFieldValue('charge_id', v);
                                                        await calculateCostAmount(setFieldValue, v, values.cost_materials, values.cost_freight);
                                                    }}
                                                    isClearable
                                                    customLabel={customLabelCharge}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                <TextField
                                                    label="Valor dos materiais"
                                                    value={values.cost_materials || ''}
                                                    onChange={handleChangeFloat}
                                                    name="cost_materials"
                                                    id="formatted-numberformat-cost_materials"
                                                    InputProps={{
                                                        inputComponent: InputComponentPrice as any
                                                    }}
                                                    fullWidth
                                                    margin="dense"
                                                    variant="outlined"
                                                    error={!!errors.cost_materials && !!dirty.cost_materials}
                                                    helperText={!!errors.cost_materials && !!dirty.cost_materials && errors.cost_materials}
                                                    onFocus={() => {
                                                        if (values.cost_materials !== values.cost_materials_old) {
                                                            setFieldValue('cost_materials_old', values.cost_materials);
                                                        }
                                                    }}
                                                    onBlur={async () => {
                                                        if (values.cost_materials !== values.cost_materials_old) {
                                                            await calculateCostAmount(setFieldValue, values.charge_id, values.cost_materials, values.cost_freight);
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                                                <TextField
                                                    label="Valor de frete"
                                                    value={values.cost_freight || ''}
                                                    onChange={handleChangeFloat}
                                                    name="cost_freight"
                                                    id="formatted-numberformat-cost_freight"
                                                    InputProps={{
                                                        inputComponent: InputComponentPrice as any
                                                    }}
                                                    fullWidth
                                                    margin="dense"
                                                    variant="outlined"
                                                    error={!!errors.cost_freight && !!dirty.cost_freight}
                                                    helperText={!!errors.cost_freight && !!dirty.cost_freight && errors.cost_freight}
                                                    onFocus={() => {
                                                        if (values.cost_freight !== values.cost_freight_old) {
                                                            setFieldValue('cost_freight_old', values.cost_freight);
                                                        }
                                                    }}
                                                    onBlur={async () => {
                                                        if (values.cost_freight !== values.cost_freight_old) {
                                                            await calculateCostAmount(setFieldValue, values.charge_id, values.cost_materials, values.cost_freight);
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={1} xl={1} display="flex" alignItems={"center"} justifyContent="center">
                                                <Box>
                                                    <Typography component={"a"}
                                                        href="https://www2.correios.com.br/sistemas/precosPrazos/"
                                                        target={"_blank"}
                                                        className='visitedBlue'
                                                        variant='caption'>
                                                        Calcular frete
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={3} xl={3} display="flex" alignItems={"center"}>
                                                <TextField
                                                    label="Valor total"
                                                    value={values.cost_amount || ''}
                                                    onChange={handleChangeFloat}
                                                    name="cost_amount"
                                                    id="formatted-numberformat-cost_amount"
                                                    InputProps={{
                                                        inputComponent: InputComponentPrice as any
                                                    }}
                                                    fullWidth
                                                    margin="dense"
                                                    variant="outlined"
                                                    error={!!errors.cost_amount && !!dirty.cost_amount}
                                                    helperText={!!errors.cost_amount && !!dirty.cost_amount && errors.cost_amount}
                                                    key={values.cost_amount}
                                                    disabled
                                                    sx={{ flex: 1 }}
                                                />
                                                <FormControlLabel
                                                    style={{ margin: 0 }}
                                                    control={
                                                        <Checkbox
                                                            name='not_charged'
                                                            size="small"
                                                            onChange={async (_, v) => {
                                                                await setFieldValue('not_charged', v || false);
                                                            }}
                                                            checked={values.not_charged === true}
                                                        />
                                                    }
                                                    label="Abono"
                                                />
                                            </Grid>
                                        </Grid>
                                        <Box marginTop={3}>
                                            <Typography variant='subtitle2' className="subtitleGradientBackgorund">Expedição</Typography>
                                        </Box>
                                        <Divider style={{ marginBottom: 4 }} />
                                        <Grid spacing={1} container>
                                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                                <FormControlLabel
                                                    style={{ margin: 0 }}
                                                    control={
                                                        <Checkbox
                                                            name='local_pickup'
                                                            size="small"
                                                            onChange={async (_, v) => {
                                                                await setFieldValue('local_pickup', v || false);
                                                                await setFieldValue('tracking_post_office', '');
                                                            }}
                                                            checked={values.local_pickup === true}
                                                            sx={{ paddingLeft: 0 }}
                                                        />
                                                    }
                                                    label="Equipamento retirado no local"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                                <FormControlLabel
                                                    style={{ margin: 0 }}
                                                    control={
                                                        <Checkbox
                                                            name='declaration_content'
                                                            size="small"
                                                            onChange={async (_, v) => {
                                                                await setFieldValue('declaration_content', v || false);
                                                                await setFieldValue('invoice_number_out', '');
                                                            }}
                                                            checked={values.declaration_content === true}
                                                            sx={{ paddingLeft: 0 }}
                                                        />
                                                    }
                                                    label="Declaração de conteúdo"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                                <FormControlLabel
                                                    style={{ margin: 0 }}
                                                    control={
                                                        <Checkbox
                                                            name='available_for_pickup'
                                                            size="small"
                                                            onChange={async (_, v) => {
                                                                await setFieldValue('available_for_pickup', v || false);
                                                            }}
                                                            checked={values.available_for_pickup === true}
                                                            sx={{ paddingLeft: 0 }}
                                                        />
                                                    }
                                                    label="Disponível para retirada"
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid spacing={1} container>
                                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                                <TextField
                                                    name="tracking_post_office"
                                                    label='Rastreio correios'
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    inputProps={{ maxLength: 50 }}
                                                    value={values.tracking_post_office}
                                                    onChange={handleChange}
                                                    error={!!errors.tracking_post_office && !!dirty.tracking_post_office}
                                                    helperText={!!errors.tracking_post_office && !!dirty.tracking_post_office && errors.tracking_post_office}
                                                    disabled={!!values.local_pickup}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                                <TextField
                                                    name="invoice_number_out"
                                                    label={'Nota fiscal de saída'}
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    inputProps={{ maxLength: 200 }}
                                                    value={values.invoice_number_out || ''}
                                                    onChange={e => {
                                                        const re = /^[0-9\b]+$/;
                                                        if (e.target.value === '' || re.test(e.target.value)) {
                                                            handleChange(e);
                                                        }
                                                    }}
                                                    disabled={!!values.declaration_content}
                                                    error={!!errors.invoice_number_out && !!dirty.invoice_number_out}
                                                    helperText={!!errors.invoice_number_out && !!dirty.invoice_number_out && errors.invoice_number_out}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                                <TextField
                                                    name="service_invoice"
                                                    label={'Nota fiscal de serviço'}
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    inputProps={{ maxLength: 200 }}
                                                    value={values.service_invoice || ''}
                                                    onChange={e => {
                                                        const re = /^[0-9\b]+$/;
                                                        if (e.target.value === '' || re.test(e.target.value)) {
                                                            handleChange(e);
                                                        }
                                                    }}
                                                    error={!!errors.service_invoice && !!dirty.service_invoice}
                                                    helperText={!!errors.service_invoice && !!dirty.service_invoice && errors.service_invoice}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                                                <DatePicker
                                                    label={'Data expedição'}
                                                    value={values.post_office_date}
                                                    onChange={(value) => setFieldValue('post_office_date', value)}
                                                    fullWidth
                                                />
                                            </Grid>
                                        </Grid>
                                        <AttachmentList
                                            attachments={values.attachments}
                                            downloadAttachment={downloadAttachment}
                                            openAttachmentDescriptionDialog={openAttachmentDescriptionDialog}
                                            removeAttachment={removeAttachment}
                                            setFieldValue={setFieldValue}
                                            addAttachment={addAttachment}
                                            title={'Anexos'}
                                            removeAddButton={isMultiAdd}
                                        />
                                        <AttachmentDescriptionFormDialog
                                            attachmentDescriptionDialogOpen={attachmentDescriptionDialogOpen}
                                            onCancel={() => closeAttachmentDescriptionDialog()}
                                            onSubmit={(description) => submitAttachmentDescriptionDialog(setFieldValue, values.attachments, description, attachmentsEditingIdx)}
                                            attachments={values.attachments}
                                            attachmentsEditingIdx={attachmentsEditingIdx}
                                        />
                                        <CommentFormDialog
                                            commentDialogOpen={commentDialogOpen}
                                            onCancel={() => closeCommentDialog()}
                                            onSubmit={(comment, isPublic) => submitCommentDialog(setFieldValue, values.comments, comment, isPublic)}
                                            allowPublicComment={user?.allow_create_public_observation}
                                        />
                                        <TechAssisComponentFormDialog
                                            open={techAssisComponentDialogOpen}
                                            onCancel={onCancelTechAssisComponentFormDialog}
                                            onSubmit={(data) => {
                                                onSubmitTechAssisComponentFormDialog(data, setFieldValue, values);
                                            }}
                                            selectedComponentsIds={values.components.map(c => c.component_id)}
                                        />
                                    </>
                                );
                            }}
                        </Form>
                        <Box zIndex={2} marginTop={3} marginBottom={4} borderBottom={`1px solid ${theme.palette.divider}`} display="flex" position={'sticky'} top={'-4px'} bgcolor={theme.palette.background.default}>
                            <Typography variant='subtitle2' style={{ flex: 1 }} className="subtitleGradientBackgorund">Histórico do atendimento</Typography>
                            {isMultiAdd === false ? <Tooltip placement="top" title={'Adicionar comentário'}>
                                <IconButton
                                    size='small'
                                    color="inherit"
                                    aria-label={'Adicionar comentário'}
                                    onClick={() => openCommentDialog()}
                                >
                                    <ChatIcon />
                                </IconButton>
                            </Tooltip> : null}
                        </Box>
                        <Divider style={{ marginBottom: 4 }} />
                        <TicketHistory
                            key={item?.id}
                            type="technicalAssistance"
                            id={item?.id}
                            comments={item?.comments || []}
                            ticketHistoryEventListener={eventEmmiter}
                        />
                        <PopupWrapper
                            title={"Observações de assistência do cliente final"}
                            dismiss={() => setFinalCustomerTechnicalAssistanceObservationDialogOpen(false)}
                            maxWidth="md"
                            fullWidth
                            show={finalCustomerTechnicalAssistanceObservationDialogOpen}
                        >
                            <Typography variant='body2' style={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{finalCustomerTechnicalAssistanceObservation}</Typography>
                        </PopupWrapper>
                        <PopupWrapper
                            title={"Observações de assistência do integrador"}
                            dismiss={() => setIntegratorTechnicalAssistanceObservationDialogOpen(false)}
                            maxWidth="md"
                            fullWidth
                            show={integratorTechnicalAssistanceObservationDialogOpen}
                        >
                            <Typography variant='body2' style={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{integratorTechnicalAssistanceObservation}</Typography>
                        </PopupWrapper>
                    </Grid>
                </Grid>
            </PanelBodyFormContent>
        </Panel >
    )
}